import React, { useState } from 'react';
import { X, Search, Phone, User, CreditCard } from 'lucide-react';
import { apiFetch } from '../../utils/apiClient';

const FindUHIDModal = ({ isOpen, onClose, onSelectUHID, onPatientSelect, currentUser }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();

    // Validate phone number
    const cleanedPhone = phoneNumber.replace(/\D/g, '');
    if (!cleanedPhone || cleanedPhone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    // Backend will filter by logged-in user ID, so users can search any phone number
    // but will only see patients they registered themselves

    setIsLoading(true);
    setError('');
    setSearchResults(null);

    try {
      const response = await apiFetch(`/backend/api/patients/phone/${cleanedPhone}`, {
        method: 'GET'
      });

      if (response.status === 401) {
        setError('Your session has expired. Please log in to search for UHIDs.');
        setSearchResults(null);
        return;
      }

      if (!response.ok) {
        setError(`Failed to fetch patient data (status ${response.status})`);
        setSearchResults(null);
        return;
      }

      const data = await response.json();

      if (data.success) {
        if (data.totalRecords === 0) {
          setError('No patients found with this phone number');
        } else {
          setSearchResults(data);
        }
      } else {
        throw new Error(data.error || 'Failed to fetch patient data');
      }
    } catch (error) {
      console.error('Search error:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPhoneNumber('');
    setSearchResults(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden animate-slide-up">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Search className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold">Find My UHID</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-6">
            <label className="flex items-center space-x-2 text-gray-700 font-medium mb-3">
              <Phone className="w-4 h-4" />
              <span>Enter Patient's Phone Number</span>
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Search for any patient you registered using their phone number
            </p>
            <div className="flex gap-3">
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  setError('');
                }}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter 10-digit phone number"
                maxLength="10"
              />
              <button
                type="submit"
                disabled={isLoading}
                className={`px-6 py-3 rounded-lg text-white font-medium transition-colors ${
                  isLoading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
              <X className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Search Results */}
          {searchResults && searchResults.totalRecords > 0 && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <p className="text-green-800 font-semibold">
                  Found {searchResults.totalRecords} patient{searchResults.totalRecords > 1 ? 's' : ''} you registered with this phone number
                </p>
              </div>

              {/* UHID Cards */}
              <div className="space-y-3">
                {searchResults.patients.map((patient, index) => (
                  <div
                    key={patient.uhid}
                    onClick={() => {
                      // Call onPatientSelect to open sidebar with patient details
                      if (onPatientSelect) {
                        onPatientSelect(patient);
                      }
                      // Also keep the old behavior for backward compatibility
                      if (onSelectUHID) {
                        onSelectUHID(patient.uhid);
                      }
                    }}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all hover:border-green-400 cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">UHID #{index + 1}</p>
                            <p className="text-xl font-mono font-bold text-green-600">
                              {patient.uhid}
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium">{patient.fullName}</span>
                          </div>

                          {patient.registrationDate && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600">Registered:</span>
                              <span className="font-medium">
                                {new Date(patient.registrationDate).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="mt-3 text-center">
                          <p className="text-xs text-green-600 font-semibold">
                            Click to select this UHID
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Results Message */}
          {searchResults && searchResults.totalRecords === 0 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600">No UHIDs found for this phone number</p>
              <p className="text-sm text-gray-500 mt-2">Please check the number or register as a new patient</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="border-t px-6 py-4 bg-gray-50">
          <button
            onClick={handleClose}
            className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FindUHIDModal;